import React, { useCallback } from "react";

import { faDownload, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";

import EmailFile from "@mapmycustomers/shared/types/email/EmailFile";
import formatFileSize from "@mapmycustomers/shared/util/formatters/formatFileSize";

import { useConfigProvider } from "../../ConfigProvider";
import FileIcon, { FileType, getFileType } from "../../FileIcon";
import LoadingSpinner from "../../LoadingSpinner";
import AttachedFileCallback from "../EmailBody/type/AttachedFileCallback";

import EmailAttachedFile from "./EmailAttachedFile";
import styles from "./EmailAttachedFileList.module.scss";

export interface EmailAttachedFileListProps {
  className?: string;
  files?: EmailAttachedFile[];
  onFileDelete?: AttachedFileCallback;
  onFileDownload?: AttachedFileCallback;
  onFilePreview?: AttachedFileCallback;
  readOnly?: boolean;
}

const getFileNameParts = (attachedFile: EmailAttachedFile) => {
  const name = attachedFile.file?.name ?? attachedFile.localFile!.name;
  const indexOfDot = name.lastIndexOf(".");
  if (indexOfDot === -1) {
    return [name, ""];
  }

  return [name.substring(0, indexOfDot + 1), name.substring(indexOfDot + 1)];
};

const EmailAttachedFileList = ({
  className,
  files,
  onFileDelete,
  onFileDownload,
  onFilePreview,
  readOnly,
}: EmailAttachedFileListProps) => {
  const configProvider = useConfigProvider();

  const handleDeleteFile = useCallback(
    (attachedFile: EmailAttachedFile) => {
      if (attachedFile.file && onFileDelete) {
        onFileDelete(attachedFile);
      }
    },
    [onFileDelete]
  );

  const isPreviewableFile = useCallback((file?: EmailFile) => {
    return file && getFileType(file) !== FileType.OTHER;
  }, []);

  const handlePreview = useCallback(
    (attachedFile: EmailAttachedFile) => {
      if (isPreviewableFile(attachedFile?.file)) {
        onFilePreview?.(attachedFile);
      }
    },
    [isPreviewableFile, onFilePreview]
  );

  if (!files || files.length === 0) {
    return null;
  }

  return (
    <div className={cn(styles.container, className)}>
      {files.map((attachedFile) => (
        <div className={styles.fileContainer} key={attachedFile.id}>
          {attachedFile.file && (
            <FileIcon
              className={styles.fileIcon}
              file={attachedFile.file}
              onClick={() => handlePreview(attachedFile)}
            />
          )}
          <div className={styles.fileName} onClick={() => handlePreview(attachedFile)}>
            <div className={styles.fileNamePure}>{getFileNameParts(attachedFile)[0]}</div>
            <div className={styles.fileNameExtra}>
              <span>{getFileNameParts(attachedFile)[1]}</span>
              <span>
                {`(${formatFileSize(
                  attachedFile.file?.contentLength ?? attachedFile.localFile!.size ?? 0
                )})`}
              </span>
            </div>
          </div>
          <div className={styles.actions}>
            {!attachedFile.uploading && (
              <Tooltip
                title={configProvider.formatMessage("ui.emailBody.attachedFileList.download")}
              >
                <FontAwesomeIcon
                  className={styles.downloadIcon}
                  icon={faDownload}
                  onClick={() => onFileDownload?.(attachedFile)}
                />
              </Tooltip>
            )}
            <span>
              {attachedFile.uploading ? (
                <LoadingSpinner mini />
              ) : (
                !readOnly && (
                  <Tooltip
                    title={configProvider.formatMessage("ui.emailBody.attachedFileList.remove")}
                  >
                    <FontAwesomeIcon
                      className={styles.deleteIcon}
                      icon={faTimes}
                      onClick={() => handleDeleteFile(attachedFile)}
                    />
                  </Tooltip>
                )
              )}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default EmailAttachedFileList;
